import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true, // include session_token cookie
    // CSRF: the session cookie is SameSite=None (pages.dev -> railway.app), so
    // the browser will attach it to cross-site requests. This custom header
    // forces a preflight that only an allowed origin can pass, which a hostile
    // page cannot. The backend rejects cookie-authenticated writes without it.
    headers: { "X-DD-App": "1" },
});

// Auth
export const authMe = () => api.get("/auth/me").then(r => r.data);
export const authLogout = () => api.post("/auth/logout").then(r => r.data);
export const authRegister = (email, password, name, originUrl) => api.post("/auth/register", { email, password, name, origin_url: originUrl }).then(r => r.data);
export const authLogin = (email, password) => api.post("/auth/login", { email, password }).then(r => r.data);
export const authVerifyEmail = (token) => api.post("/auth/verify-email", { token }).then(r => r.data);
export const authResendVerification = (email, originUrl) => api.post("/auth/resend-verification", { email, origin_url: originUrl }).then(r => r.data);
export const authForgotPassword = (email, originUrl) => api.post("/auth/forgot-password", { email, origin_url: originUrl }).then(r => r.data);
export const authResetPassword = (token, password) => api.post("/auth/reset-password", { token, new_password: password }).then(r => r.data);
export const submitAIReport = (data) => api.post("/ai-reports", data).then(r => r.data);
export const submitContactMessage = (data) => api.post("/contact-messages", data).then(r => r.data);
export const adminMe = () => api.get("/admin/me").then(r => r.data);
export const adminListReports = () => api.get("/admin/ai-reports").then(r => r.data);
export const adminListMessages = () => api.get("/admin/contact-messages").then(r => r.data);
export const adminUpdateReportStatus = (id, status) => api.patch(`/admin/ai-reports/${id}`, { status }).then(r => r.data);
export const adminUpdateMessageStatus = (id, status) => api.patch(`/admin/contact-messages/${id}`, { status }).then(r => r.data);
export const setBackground = (url) => api.put("/me/background", { url }).then(r => r.data);
export const clearBackground = () => api.delete("/me/background").then(r => r.data);
export const uploadBackground = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/me/background/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const updateSettings = (data) => api.patch("/me/settings", data).then(r => r.data);
export const cancelSubscription = () => api.post("/billing/cancel").then(r => r.data);
export const resumeSubscription = () => api.post("/billing/resume").then(r => r.data);
export const deleteAccount = () => api.delete("/me").then(r => r.data);
export const authGoogle = (idToken) => api.post("/auth/google", { id_token: idToken }).then(r => r.data);

// Novels
export const listNovels = () => api.get("/novels").then(r => r.data);
export const createNovel = (data) => api.post("/novels", data).then(r => r.data);
export const getNovel = (id) => api.get(`/novels/${id}`).then(r => r.data);
export const updateNovel = (id, data) => api.put(`/novels/${id}`, data).then(r => r.data);
export const deleteNovel = (id) => api.delete(`/novels/${id}`).then(r => r.data);

// Chapters
export const listChapters = (novelId) => api.get(`/novels/${novelId}/chapters`).then(r => r.data);
export const createChapter = (novelId, data) => api.post(`/novels/${novelId}/chapters`, data).then(r => r.data);
export const updateChapter = (id, data) => api.put(`/chapters/${id}`, data).then(r => r.data);
export const deleteChapter = (id) => api.delete(`/chapters/${id}`).then(r => r.data);

// Scenes
export const listScenes = (chapterId) => api.get(`/chapters/${chapterId}/scenes`).then(r => r.data);
export const createScene = (chapterId, data) => api.post(`/chapters/${chapterId}/scenes`, data).then(r => r.data);
export const updateScene = (id, data) => api.put(`/scenes/${id}`, data).then(r => r.data);
export const deleteScene = (id) => api.delete(`/scenes/${id}`).then(r => r.data);

// Cover
export const uploadCover = (novelId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/novels/${novelId}/cover/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
};
export const deleteCover = (novelId) =>
    api.delete(`/novels/${novelId}/cover`).then(r => r.data);
// Build absolute URL for an image path returned by backend (e.g. /api/novels/.../cover-image?v=...)
export const absUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    if (path.startsWith("data:")) return path;
    return `${BACKEND_URL}${path}`;
};

// Share / comments
export const enableShare = (novelId) => api.post(`/novels/${novelId}/share`).then(r => r.data);
export const disableShare = (novelId) => api.delete(`/novels/${novelId}/share`).then(r => r.data);
export const listNovelComments = (novelId) => api.get(`/novels/${novelId}/comments`).then(r => r.data);
export const deleteComment = (id) => api.delete(`/comments/${id}`).then(r => r.data);

// Public reader (no auth)
const publicApi = axios.create({ baseURL: API });
const publicApiBilling = axios.create({ baseURL: API });
export const shareNovel = (token) => publicApi.get(`/share/${token}/novel`).then(r => r.data);
export const shareChapters = (token) => publicApi.get(`/share/${token}/chapters`).then(r => r.data);
export const shareComments = (token) => publicApi.get(`/share/${token}/comments`).then(r => r.data);
export const addShareComment = (token, payload) => publicApi.post(`/share/${token}/comments`, payload).then(r => r.data);

// Proofread structured
export const proofreadJSON = (text, model = "claude", novelId, chapterId) =>
    api.post("/ai/proofread-json", { text, model, novel_id: novelId, chapter_id: chapterId }).then(r => r.data);

// Versions
export const listVersions = (chapterId) => api.get(`/chapters/${chapterId}/versions`).then(r => r.data);
export const getVersion = (chapterId, vid) => api.get(`/chapters/${chapterId}/versions/${vid}`).then(r => r.data);
export const restoreVersion = (chapterId, vid) => api.post(`/chapters/${chapterId}/versions/${vid}/restore`).then(r => r.data);

// Characters
export const listCharacters = (novelId) => api.get(`/novels/${novelId}/characters`).then(r => r.data);
export const createCharacter = (novelId, data) => api.post(`/novels/${novelId}/characters`, data).then(r => r.data);
export const updateCharacter = (id, data) => api.put(`/characters/${id}`, data).then(r => r.data);
export const deleteCharacter = (id) => api.delete(`/characters/${id}`).then(r => r.data);

// Character color palette (mirrors backend CHARACTER_PALETTE)
export const CHARACTER_PALETTE = [
    "#7E9B7A", "#5A7B89", "#9B6B7E", "#C28F4D", "#3F7F7B",
    "#7C5A8E", "#B5613A", "#4F6E4A", "#5965A6", "#A48A3F",
];

// Plot
export const listPlot = (novelId) => api.get(`/novels/${novelId}/plot`).then(r => r.data);
export const createPlot = (novelId, data) => api.post(`/novels/${novelId}/plot`, data).then(r => r.data);
export const deletePlot = (id) => api.delete(`/plot/${id}`).then(r => r.data);

// Stats / streak
export const novelStats = (novelId) => api.get(`/novels/${novelId}/stats`).then(r => r.data);
export const logWritingSession = (novelId, words) =>
    api.post("/writing-sessions/log", { novel_id: novelId, words }).then(r => r.data);

// Billing
export const billingPlans = () => publicApiBilling.get("/billing/plans").then(r => r.data);
export const billingMe = () => api.get("/billing/me").then(r => r.data);
export const createCheckout = (packageId, originUrl) =>
    api.post("/billing/checkout", { package_id: packageId, origin_url: originUrl }).then(r => r.data);
export const billingStatus = (sessionId) =>
    api.get(`/billing/status/${sessionId}`).then(r => r.data);

// Reorder
export const reorderChapters = (novelId, ids) =>
    api.post(`/novels/${novelId}/chapters/reorder`, { ids }).then(r => r.data);
export const reorderScenes = (chapterId, ids) =>
    api.post(`/chapters/${chapterId}/scenes/reorder`, { ids }).then(r => r.data);

// Export
export const exportNovelUrl = (novelId, format) => `${API}/novels/${novelId}/export?format=${format}`;

// The error streamAI hands to onError. It used to be just "HTTP 429", which
// threw away the server's message: a user who ran out of AI words saw
// "AI error: HTTP 429" instead of being told so and offered more.
// `reason` is "ai_words_exhausted" when the allowance is used up — the only
// 429 that should open the upgrade dialog (others mean "slow down").
async function streamError(res) {
    let detail = "";
    try {
        const body = await res.json();
        detail = typeof body?.detail === "string" ? body.detail : "";
    } catch { /* non-JSON body */ }
    return {
        status: res.status,
        reason: res.headers.get("X-DD-Reason") || "",
        detail,
        toString() { return detail || `HTTP ${res.status}`; },
    };
}

// Streaming AI
export async function streamAI(payload, onDelta, onDone, onError) {
    try {
        const res = await fetch(`${API}/ai/stream`, {
            method: "POST",
            // This call does not go through the axios instance above, so it
            // has to set the CSRF header itself. Without it the backend
            // rejects every cookie-authenticated write with 403 — which is
            // exactly what happened: Write, Continue and Edit were all dead
            // in production while proofread, which uses axios, kept working.
            headers: { "Content-Type": "application/json", "X-DD-App": "1" },
            credentials: "include",
            body: JSON.stringify(payload),
        });
        if (!res.ok || !res.body) {
            onError?.(await streamError(res));
            return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
                if (!line.startsWith("data:")) continue;
                const json = line.slice(5).trim();
                if (!json) continue;
                try {
                    const evt = JSON.parse(json);
                    if (evt.delta) onDelta?.(evt.delta);
                    else if (evt.error) onError?.(evt.error);
                    else if (evt.done) { onDone?.(); return; }
                } catch (e) { console.warn("Malformed SSE frame ignored", e); }
            }
        }
        onDone?.();
    } catch (e) {
        onError?.(e.message || String(e));
    }
}
